import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { DealApplicationRequirement } from 'app/services/deals/deal-application-requirement.model';
import { catchError, map, publishLast, refCount, share, tap } from 'rxjs/operators';
import { GetDealQuestionRequirements, UpdateAnswers, ClearApplicationRequirements } from './deal-application-requirement.actions';
import _ from 'lodash';
import { QuestionService } from 'app/services/question/question.service';
import { DealApplicationRequirementUtility } from './deal-application-requirement-utility';
import { throwError, forkJoin, Observable, AsyncSubject } from 'rxjs';
import { Injectable } from '@angular/core';

export interface DealApplicationQuestionRequirement {
  application: DealApplicationRequirement[];
  fulfilledCount: number;
  missingFieldNames: string;
  percentComplete: number;
  requiredCount: number;
}

export class DealApplicationRequirementStateModel {
  loading: boolean;
  dealApplicationRequirements: {
    dealId: number;
    questions: DealApplicationQuestionRequirement | null;
  }[];
}

@State<DealApplicationRequirementStateModel>({
	name: 'dealApplicationRequirements',
  defaults: {
    loading: false,
    dealApplicationRequirements: [],
	}
})

@Injectable()

export class DealApplicationRequirementState {

  @Selector()
  static dealQuestionRequirements(state: DealApplicationRequirementStateModel) {
    return (dealId: number) => {
      const dealQuestionRequirements = state.dealApplicationRequirements.find(requirements => requirements.dealId === dealId);
      return dealQuestionRequirements ? dealQuestionRequirements.questions : null;
    };
  }

  @Selector()
  static loading(state: DealApplicationRequirementStateModel) {
    return state.loading;
  }

  @Selector()
  static requirementsMet(state: DealApplicationRequirementStateModel) {
    return (dealId: number) => {
      const dealDocumentRequirements = state.dealApplicationRequirements.find(requirements => requirements.dealId === dealId);
      return dealDocumentRequirements && dealDocumentRequirements.questions
        ? dealDocumentRequirements.questions.percentComplete === 100
        : false;
    };
  }

  private answeredQuestions$: Observable<any> | null = null;

  constructor(
    private questionService: QuestionService,
    private dealApplicationRequirementUtility: DealApplicationRequirementUtility,
    private store: Store
  ) {}

  @Action(GetDealQuestionRequirements)
	getDealQuestionRequirements(
    { getState, patchState }: StateContext<DealApplicationRequirementStateModel>,
    { deal, refresh = false }: GetDealQuestionRequirements
  ) {
    const dealAlreadyInState = getState().dealApplicationRequirements.find(requirements => requirements.dealId === deal.id);
    if (dealAlreadyInState && !refresh) {
      return;
    }

    const applicationQuestions$ = this.questionService.getQuestionRequirements(deal.loanProductId);
    if (!this.answeredQuestions$) {
      this.answeredQuestions$ = this.questionService.getAnsweredQuestions(deal.borrowerId).pipe(
        share({ connector: () => new AsyncSubject(), resetOnError: false, resetOnComplete: false, resetOnRefCountZero: false })
      );
    }

    patchState({ loading: true });

    return forkJoin([
      applicationQuestions$,
      this.answeredQuestions$
    ]).pipe(
      map(([applicationQuestions, answeredQuestions]) => {
        const questions = this.dealApplicationRequirementUtility.annotateProductApplication(applicationQuestions, answeredQuestions);
        let dealApplicationRequirements;

        if (dealAlreadyInState) {
          const dealIndex = getState().dealApplicationRequirements.findIndex(requirements => requirements.dealId === deal.id);
          const dealRequirementsCopy = { ...getState().dealApplicationRequirements[dealIndex] };
          const stateCopy =  [ ...getState().dealApplicationRequirements ];
          dealRequirementsCopy.questions = questions;
          stateCopy[dealIndex] = dealRequirementsCopy;
          dealApplicationRequirements = stateCopy;
        } else {
          dealApplicationRequirements = [
            ...getState().dealApplicationRequirements,
            { dealId: deal.id, questions }
          ];
        }

        patchState({
          loading: false,
          dealApplicationRequirements
        });
      })
    );
  }

  @Action(UpdateAnswers)
	updateAnswers({ patchState }: StateContext<DealApplicationRequirementStateModel>, { deal, updatedFields }: UpdateAnswers) {
    patchState({ loading: true });
    return this.questionService.updateAnswers(deal.borrowerId, updatedFields).pipe(catchError(err => throwError(() => err)), tap(() => {
      this.answeredQuestions$ = null;
      this.store.dispatch(new GetDealQuestionRequirements(deal, true));
    }));
  }

  @Action(ClearApplicationRequirements)
  clearApplicationRequirements({ patchState }: StateContext<DealApplicationRequirementStateModel>, {}: ClearApplicationRequirements) {
    patchState({ dealApplicationRequirements: [] });
    this.answeredQuestions$ = null;
  }
}
